import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Share2, Settings, ArrowLeft, Trophy, Network, MoreVertical, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { CompetitionConfigModal } from './CompetitionConfigModal';
import { AddPlayerRankingModal } from './AddPlayerRankingModal';
import { EnrollmentManagement } from './EnrollmentManagement';
import { CreateCompetitionModal } from './CreateCompetitionModal';
import { RankingOptionsModal } from './RankingOptionsModal';
import { RankingRulesModal } from './RankingRulesModal';
import { MatchFeedTab } from './MatchFeedTab';
import BracketView from './BracketView';
import { DrawsTab } from './DrawsTab';
import toast from 'react-hot-toast';
import './RankingTournament.css';





export const TournamentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'draws' | 'feed' | 'brackets' | 'history' | 'enrollments'>('overview');
  const [filterRound, setFilterRound] = useState<number | undefined>(undefined);
  const [bracketTab, setBracketTab] = useState<'main' | 'secondary'>('main');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [configOpenCategory, setConfigOpenCategory] = useState<string | null>(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddPlayerOpen, setIsAddPlayerOpen] = useState(false);
  

  
  const [competition, setCompetition] = useState<any>(null);

  const [loading, setLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/api/competitions/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(async data => {
      try {
        const matchesRes = await fetch(`${API_URL}/api/competitions/${id}/matches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (matchesRes.ok) {
          data.matches = await matchesRes.json();
        }
      } catch (e) {
        console.error('Failed to fetch matches for bracket', e);
      }
      setCompetition(data);
      if (data.config?.categories && data.config.categories.length > 0 && !activeCategory) {
        setActiveCategory(data.config.categories[0]);
      }
      setLoading(false);
    })
    .catch(err => {
      console.error(err);
      toast.error('Erro ao carregar torneio');
      setLoading(false);
    });
  }, [id]);

  const handleEdit = async (data: any) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Falha ao atualizar');
      const updated = await res.json();
      setCompetition((prev: any) => ({ ...updated, participants: prev?.participants || [] }));
      setIsEditModalOpen(false);
      toast.success('Torneio atualizado com sucesso!');
    } catch(err) {
      console.error(err);
      toast.error('Erro ao atualizar torneio');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Falha ao excluir');
      toast.success('Torneio excluído com sucesso!');
      navigate('/tournaments');
    } catch(err) {
      console.error(err);
      toast.error('Erro ao excluir torneio');
    }
  };

  const handleAddPlayer = async (player: { email: string; points: number }) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/${id}/participants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email: player.email, points: player.points })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Falha ao adicionar jogador');
      }
      
      toast.success('Jogador adicionado com sucesso!');
      setIsAddPlayerOpen(false);
      
      // Refresh data
      fetch(`${API_URL}/api/competitions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setCompetition(data);

      });
    } catch(err: any) {
      console.error(err);
      toast.error(err.message || 'Erro ao adicionar jogador');
    }
  };


  const handleToggleCategoryStatus = async (catName: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ONGOING' ? 'DRAFT' : 'ONGOING';
    const message = newStatus === 'ONGOING' 
      ? `Tem certeza que deseja iniciar a categoria ${catName}? Ela ficará visível para os jogadores.`
      : `Tem certeza que deseja pausar/despublicar a categoria ${catName}? Ela ficará invisível para os jogadores.`;
      
    if (!window.confirm(message)) return;

    try {
      const currentConfig = competition?.config || {};
      const catConfig = currentConfig.categoriesConfig?.[catName] || {};
      const newConfig = {
        ...currentConfig,
        categoriesConfig: {
          ...currentConfig.categoriesConfig,
          [catName]: { ...catConfig, status: newStatus }
        }
      };

      const isGlobalDraft = competition?.status === 'DRAFT';
      const body: any = { config: newConfig };
      if (newStatus === 'ONGOING' && isGlobalDraft) {
        body.status = 'ONGOING';
      }

      const res = await fetch(`${API_URL}/api/competitions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Falha ao atualizar');
      const updated = await res.json();
      setCompetition((prev: any) => ({ ...updated, participants: prev?.participants || [] }));
      toast.success(`Categoria ${catName} atualizada!`);
    } catch(err) {
      console.error(err);
      toast.error('Erro ao atualizar categoria');
    }
  };

  const handleEnrollCategory = async (catName: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/${id}/enroll`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ category: catName })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Falha ao se inscrever');
      }
      toast.success(`Inscrição na categoria ${catName} solicitada com sucesso!`);
      // Update local state to reflect new participant
      const newPart = await res.json();
      setCompetition((prev: any) => ({
        ...prev,
        participants: [...(prev?.participants || []), newPart]
      }));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Erro ao se inscrever');
    }
  };

  const canConfigure = currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR_COMPETICAO' || competition?.managerId === currentUser?.id;

  return (
    <div className="rt-container">
      <div className="rt-card">
        
        {/* Header */}
        <div className="rt-header">
          <div className="rt-header-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => navigate(-1)} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} title="Voltar">
              <ArrowLeft size={24} />
            </button>
            {competition?.imageUrl ? (
              <img src={competition.imageUrl} alt={competition.name} style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover' }} />
            ) : (
              <Trophy color="var(--primary-color)" size={24} />
            )}
            <div>
              <h1>{loading ? 'Carregando...' : competition?.name || 'Torneios e Chaves'}</h1>
              <p className="rt-header-subtitle">Chave de {competition?.config?.startDate ? new Date(competition.config.startDate).toLocaleString('pt-BR', {timeZone: 'UTC', month: 'long'}) : 'Abril'}</p>
            </div>
          </div>
          <div className="rt-header-actions">
            <button onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success('Link copiado para a área de transferência!');
            }} title="Compartilhar"><Share2 size={20} /></button>
            <button onClick={() => setIsRulesOpen(true)} title="Regulamento"><Shield size={20} /></button>
            {canConfigure && (
              <>
                <button onClick={() => setIsConfigOpen(true)} title="Configurações do Torneio">
                  <Settings size={20} />
                </button>
                <button onClick={() => setIsOptionsOpen(true)} title="Mais Opções">
                  <MoreVertical size={20} />
                </button>
              </>
            )}
          </div>
        </div>


        {/* Tabs */}
        <div className="rt-tabs">
          <button 
            className={"rt-tab " + (activeTab === 'overview' ? 'active' : '')}
            onClick={() => { setActiveTab('overview'); setFilterRound(undefined); }}
          >
            Visão Geral
          </button>
          {canConfigure && (
            <button 
              className={"rt-tab " + (activeTab === 'draws' ? 'active' : '')}
              onClick={() => { setActiveTab('draws'); setFilterRound(undefined); }}
            >
              Sorteios
            </button>
          )}
          <button 
            className={"rt-tab " + (activeTab === 'feed' ? 'active' : '')}
            onClick={() => { setActiveTab('feed'); setFilterRound(undefined); }}
          >
            Partidas
          </button>
          <button 
            className={"rt-tab " + (activeTab === 'brackets' ? 'active' : '')}
            onClick={() => setActiveTab('brackets')}
          >
            Chaves
          </button>
          <button 
            className={"rt-tab " + (activeTab === 'history' ? 'active' : '')}
            onClick={() => setActiveTab('history')}
          >
            Meus Jogos
          </button>
          {canConfigure && (
            <button 
              className={"rt-tab " + (activeTab === 'enrollments' ? 'active' : '')}
              onClick={() => setActiveTab('enrollments')}
            >
              Inscrições & Pagamentos
            </button>
          )}
        </div>

        {/* Category Selector for non-overview tabs */}
        {activeTab !== 'overview' && competition?.config?.categories?.length > 0 && (
          <div style={{ padding: '16px 24px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Exibindo categoria:</span>
              <select 
                value={activeCategory || ''}
                onChange={(e) => setActiveCategory(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', minWidth: '150px' }}
              >
                {competition?.config?.categories?.map((c: string) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="rt-content" style={{overflowX: 'auto'}}>

          {activeTab === 'overview' && id && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div style={{ padding: '20px', background: 'var(--bg-panel)', borderRadius: '12px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Network size={20} color="var(--primary-color)" />
                  Categorias
                </h2>
                {competition?.config?.categories && competition.config.categories.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                    {competition.config.categories.map((cat: string) => {
                      const catConfig = competition.config.categoriesConfig?.[cat] || {};
                      const format = catConfig.tournamentRules?.drawFormat || competition.config.tournamentRules?.drawFormat || 'SINGLE_ELIMINATION';
                      const formatText = format === 'SINGLE_ELIMINATION' ? 'Eliminação Simples' : format === 'DOUBLE_ELIMINATION' ? 'Dupla Eliminação' : 'Fase de Grupos';
                      const enrolledCount = competition.participants?.filter((p: any) => p.category === cat).length || 0;
                      const catStatus = catConfig.status || 'DRAFT';
                      
                      // Filter DRAFT categories for normal users
                      if (!canConfigure && catStatus !== 'ONGOING') return null;

                      // Check if user is enrolled
                      const isUserEnrolled = !!competition.participants?.find((p: any) => p.userId === currentUser?.id && p.category === cat);
                      
                      return (
                        <div key={cat} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {catConfig.logoUrl && (
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', overflow: 'hidden' }}>
                                  {catConfig.logoUrl.startsWith('http') ? (
                                    <img src={catConfig.logoUrl} alt={cat} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <span>{catConfig.logoUrl}</span>
                                  )}
                                </div>
                              )}
                              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>{cat}</h3>
                            </div>
                            {canConfigure && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button 
                                  onClick={() => handleToggleCategoryStatus(cat, catStatus)}
                                  style={{ 
                                    background: catStatus === 'ONGOING' ? 'rgba(235,87,87,0.2)' : 'rgba(16,185,129,0.2)', 
                                    border: `1px solid ${catStatus === 'ONGOING' ? '#EB5757' : '#10B981'}`, 
                                    color: catStatus === 'ONGOING' ? '#EB5757' : '#10B981', 
                                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' 
                                  }}
                                >
                                  {catStatus === 'ONGOING' ? 'Pausar' : 'Iniciar Categoria'}
                                </button>
                                <button 
                                  onClick={() => { setConfigOpenCategory(cat); setIsConfigOpen(true); }}
                                  style={{ background: 'transparent', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', padding: '4px' }}
                                  title="Configurar Categoria"
                                >
                                  <Settings size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                          
                          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span>{formatText}</span>
                            <span>{enrolledCount} inscritos</span>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                            <button 
                              onClick={() => {
                                setActiveCategory(cat);
                                setActiveTab(competition.status === 'ONGOING' ? 'brackets' : 'draws');
                              }}
                              style={{ 
                                flex: 1, background: 'var(--primary-color)', color: 'white', border: 'none', 
                                padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' 
                              }}
                            >
                              Ver Detalhes
                            </button>
                            
                            {!canConfigure && currentUser?.role === 'CLIENTE' && !isUserEnrolled && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEnrollCategory(cat);
                                }}
                                style={{ 
                                  flex: 1, background: 'var(--primary-color)', color: 'black', border: 'none', 
                                  padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                              >
                                Inscrever-se
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: 'rgba(255,255,255,0.5)' }}>Nenhuma categoria definida. Vá em "Configurações do Torneio" para criar categorias.</p>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'draws' && id && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <DrawsTab 
                competitionId={id} 
                currentUser={currentUser} 
                competition={competition} 
                activeCategory={activeCategory}
                onViewMatches={(round) => {
                  setFilterRound(round);
                  setActiveTab('feed');
                }} 
              />
            </motion.div>
          )}

          {activeTab === 'feed' && id && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                {filterRound && (
                  <div style={{ background: 'var(--primary-color)', color: 'black', padding: '4px 12px', borderRadius: '12px', fontWeight: 'bold' }}>
                    Filtrando: Rodada {filterRound}
                    <button onClick={() => setFilterRound(undefined)} style={{ background: 'transparent', border: 'none', marginLeft: '8px', cursor: 'pointer' }}>×</button>
                  </div>
                )}
              </div>
              <MatchFeedTab competitionId={id} currentUser={currentUser} competition={competition} filterRound={filterRound} activeCategory={activeCategory} />
            </motion.div>
          )}

          {activeTab === 'history' && id && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <MatchFeedTab competitionId={id} currentUser={currentUser} competition={competition} filterPlayerId={currentUser?.id} activeCategory={activeCategory} />
            </motion.div>
          )}

          {activeTab === 'brackets' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {competition?.matches && competition.matches.length > 0 ? (
                <>

                  {competition.config?.tournamentRules?.drawFormat === 'DOUBLE_ELIMINATION' && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                      <button 
                        onClick={() => setBracketTab('main')}
                        style={{ 
                          background: bracketTab === 'main' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                          color: 'white', border: 'none', padding: '8px 24px', borderRadius: '20px', cursor: 'pointer', fontWeight: 600, transition: '0.2s'
                        }}
                      >
                        Chave Principal
                      </button>
                      <button 
                        onClick={() => setBracketTab('secondary')}
                        style={{ 
                          background: bracketTab === 'secondary' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                          color: 'white', border: 'none', padding: '8px 24px', borderRadius: '20px', cursor: 'pointer', fontWeight: 600, transition: '0.2s'
                        }}
                      >
                        Chave Secundária
                      </button>
                    </div>
                  )}
                  <BracketView 
                    matches={competition.matches.filter((m: any) => {
                      const stageMatch = bracketTab === 'main' ? !m.stage?.startsWith('SEC:') : m.stage?.startsWith('SEC:');
                      const categoryMatch = activeCategory ? m.category === activeCategory : true;
                      return stageMatch && categoryMatch;
                    })} 
                    participants={competition?.participants || []} 
                    competition={competition}
                  />
                </>
              ) : (
                <div className="rt-empty-state">
                  <Network size={48} />
                  <p>A chave não foi inicializada.</p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'enrollments' && canConfigure && (
            <EnrollmentManagement 
              competitionId={id!}
              participants={competition?.participants || []} 
              categories={competition?.config?.categories}
              onUpdate={() => {
                fetch(`${API_URL}/api/competitions/${id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                }).then(res => res.json()).then(data => {
                  setCompetition(data);
                });
              }} 
            />
          )}
          
        </div>

      </div>

      <AddPlayerRankingModal 
        isOpen={isAddPlayerOpen}
        onClose={() => setIsAddPlayerOpen(false)}
        onAdd={handleAddPlayer}
      />

      <CompetitionConfigModal
        isOpen={isConfigOpen}
        onClose={() => { setIsConfigOpen(false); setConfigOpenCategory(null); }}
        competitionType="TOURNAMENT"
        initialConfig={competition?.config || {}}
        initialExpandedCategory={configOpenCategory}
        onSave={async (newConfig) => {
          try {
            const res = await fetch(`${API_URL}/api/competitions/${id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ config: newConfig })
            });
            if (res.ok) {
              const updated = await res.json();
              setCompetition((prev: any) => ({ ...updated, participants: prev?.participants || [] }));
              toast.success('Configurações avançadas salvas com sucesso!');
            } else {
              toast.error('Erro ao salvar as configurações.');
            }
          } catch(err) {
            console.error(err);
            toast.error('Erro de conexão ao salvar configurações.');
          }
        }}
      />


      
      <RankingOptionsModal 
        isOpen={isOptionsOpen} 
        onClose={() => setIsOptionsOpen(false)} 
        canEdit={canConfigure} 
        title="Opções do Torneio"
        onEdit={() => setIsEditModalOpen(true)}
        onDelete={() => { if(window.confirm('Tem certeza que deseja excluir esta competição?')) handleDelete(); }}
      />
      
      <CreateCompetitionModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        type="TOURNAMENT"
        initialData={competition}
        onCreate={handleEdit}
      />

      <RankingRulesModal
        isOpen={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
        config={competition?.config}
        canEdit={canConfigure}
        onSave={async (newRules) => {
          try {
            const token = localStorage.getItem('tt_auth_token');
            const res = await fetch(`${API_URL}/api/competitions/${competition?.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                config: {
                  ...competition?.config,
                  customRulesText: newRules
                }
              })
            });
            if (res.ok) {
              const updated = await res.json();
              setCompetition((prev: any) => ({ ...updated, participants: prev?.participants || [] }));
              toast.success('Regulamento atualizado com sucesso!');
              setIsRulesOpen(false);
            } else {
              toast.error('Erro ao salvar regulamento.');
            }
          } catch (err) {
            console.error(err);
            toast.error('Erro de conexão ao salvar regulamento.');
          }
        }}
      />
    </div>
  );
};
